import { auth, db } from '../../firebase/init';
import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, type Unsubscribe } from 'firebase/firestore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FUNCTIONS_BASE_URL = (import.meta as any).env?.VITE_TRUSTGATE_FUNCTIONS_URL ||
  'https://us-central1-trustgate-27ddc.cloudfunctions.net';

export function getPublicPolicyPackArtifactUrl(packId: string, version: string): string {
  return `${FUNCTIONS_BASE_URL}/publicPolicyPacks/${encodeURIComponent(packId)}/${encodeURIComponent(version)}/artifact`;
}

async function callPublicFunction<T>(
  endpoint: string,
  method: 'GET'
): Promise<{ ok: boolean; data?: T; error?: string; code?: string; raw?: unknown }> {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();

    if (!response.ok) {
      return {
        ok: false,
        error: data.message || data.error || 'Request failed',
        code: data.code || `HTTP_${response.status}`,
        raw: data,
      };
    }

    return { ok: true, data };
  } catch (error) {
    console.error(`[PolicyPacksService] public ${endpoint} failed:`, error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Network error',
      code: 'NETWORK_ERROR',
    };
  }
}
async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch (error) {
    console.error('[PolicyPacksService] Failed to get auth token:', error);
    return null;
  }
}

async function callFunction<T>(
  endpoint: string,
  method: 'GET' | 'POST',
  body?: unknown
): Promise<{ ok: boolean; data?: T; error?: string; code?: string; raw?: unknown }> {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { ok: false, error: 'Not authenticated', code: 'UNAUTHORIZED' };
    }

    const url = `${FUNCTIONS_BASE_URL}${endpoint}`;
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);
    const data = await response.json();

    if (!response.ok) {
      return {
        ok: false,
        error: data.message || data.error || 'Request failed',
        code: data.code || `HTTP_${response.status}`,
        raw: data,
      };
    }

    return { ok: true, data };
  } catch (error) {
    console.error(`[PolicyPacksService] ${endpoint} failed:`, error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Network error',
      code: 'NETWORK_ERROR',
    };
  }
}

export interface PolicyPackSummary {
  packId: string;
  name: string;
  description: string;
  publisher: string;
  tier: 'free' | 'paid' | string;
  priceModuleId: string | null;
  latestVersion: string | null;
  latestHash: string | null;
  tags: string[];
}

export interface ListPolicyPacksResponse {
  ok: boolean;
  packs: PolicyPackSummary[];
  correlationId?: string;
}

export async function listPolicyPacks(limit = 50): Promise<{ ok: boolean; packs: PolicyPackSummary[]; error?: string }> {
  const response = await callFunction<ListPolicyPacksResponse>(
    `/listPolicyPacks?limit=${encodeURIComponent(String(limit))}`,
    'GET'
  );

  if (!response.ok || !response.data) {
    return { ok: false, packs: [], error: response.error || 'Failed to list policy packs' };
  }

  return { ok: true, packs: response.data.packs || [] };
}

export interface GetPolicyPackVersionResponse {
  ok: boolean;
  pack: {
    packId: string;
    name: string;
    description: string;
    publisher: string;
    tier: 'free' | 'paid' | string;
    priceModuleId: string | null;
    tags: string[];
  };
  version: {
    version: string;
    canonicalHash: string | null;
    signature: string | null;
    createdAt: unknown;
    packJson?: unknown;
  };
  access: {
    entitled: boolean;
    content_included: boolean;
  };
  correlationId?: string;
}

export async function getPolicyPackVersion(
  packId: string,
  version: string
): Promise<{ ok: boolean; data?: GetPolicyPackVersionResponse; error?: string }> {
  const response = await callFunction<GetPolicyPackVersionResponse>(
    `/getPolicyPackVersion?packId=${encodeURIComponent(packId)}&version=${encodeURIComponent(version)}`,
    'GET'
  );

  if (!response.ok || !response.data) {
    return { ok: false, error: response.error || 'Failed to load policy pack version' };
  }

  return { ok: true, data: response.data };
}

export interface PolicyPackVerificationMetadata {
  packId: string;
  version: string;
  canonicalHash: string;
  manifest: Record<string, unknown>;
  publisher: {
    keyId: string;
    algorithm: 'ed25519';
    publicKey: string;
  };
  signature: {
    algorithm: 'ed25519';
    signedPayloadType: string;
    signature: string;
  };
}

interface PublicPolicyPackMetadataResponse {
  ok: boolean;
  packId: string;
  version: string;
  canonicalHash: string;
  manifest: Record<string, unknown>;
  publisher: {
    keyId: string;
    algorithm: 'ed25519';
    publicKey: string;
  };
  signature: {
    algorithm: 'ed25519';
    signedPayloadType: string;
    signature: string;
  };
  correlationId?: string;
}

export async function resolvePublicPolicyPackVerificationMetadata(
  packId: string,
  version: string
): Promise<{ ok: true; data: PolicyPackVerificationMetadata } | { ok: false; error: string; code?: string }> {
  const response = await callPublicFunction<PublicPolicyPackMetadataResponse>(
    `/publicPolicyPacks/${encodeURIComponent(packId)}/${encodeURIComponent(version)}`,
    'GET'
  );

  if (!response.ok || !response.data) {
    return {
      ok: false,
      error: response.error || 'Failed to resolve public policy pack metadata',
      code: response.code,
    };
  }

  const metadata = response.data;
  if (typeof metadata.canonicalHash !== 'string' || metadata.canonicalHash.trim().length === 0) {
    return { ok: false, error: 'Resolved policy pack metadata is missing canonicalHash.', code: 'INVALID_METADATA' };
  }
  if (!metadata.manifest || typeof metadata.manifest !== 'object' || Array.isArray(metadata.manifest)) {
    return { ok: false, error: 'Resolved policy pack metadata is missing manifest.', code: 'INVALID_METADATA' };
  }
  if (!metadata.publisher || typeof metadata.publisher.publicKey !== 'string' || typeof metadata.publisher.keyId !== 'string') {
    return { ok: false, error: 'Resolved policy pack metadata is missing publisher metadata.', code: 'INVALID_METADATA' };
  }
  if (!metadata.signature || typeof metadata.signature.signature !== 'string' || typeof metadata.signature.signedPayloadType !== 'string') {
    return { ok: false, error: 'Resolved policy pack metadata is missing signature metadata.', code: 'INVALID_METADATA' };
  }

  return {
    ok: true,
    data: {
      packId: metadata.packId,
      version: metadata.version,
      canonicalHash: metadata.canonicalHash,
      manifest: metadata.manifest,
      publisher: metadata.publisher,
      signature: metadata.signature,
    },
  };
}

export async function resolvePolicyPackVerificationMetadata(
  packId: string,
  version: string
): Promise<{ ok: true; data: PolicyPackVerificationMetadata } | { ok: false; error: string; code?: string }> {
  return resolvePublicPolicyPackVerificationMetadata(packId, version);
}
export interface RequestActivatePolicyPackResponse {
  ok: boolean;
  requestId: string;
  status: 'PENDING';
  packId: string;
  version: string;
  canonicalHash: string;
  correlationId?: string;
}

export async function requestActivatePolicyPack(params: {
  packId: string;
  version: string;
  canonicalHash: string;
}): Promise<
  | { ok: true; data: RequestActivatePolicyPackResponse }
  | { ok: false; code?: string; error: string }
> {
  const response = await callFunction<RequestActivatePolicyPackResponse>(
    '/requestActivatePolicyPack',
    'POST',
    {
      packId: params.packId,
      version: params.version,
      canonicalHash: params.canonicalHash,
    }
  );

  if (!response.ok || !response.data) {
    return {
      ok: false,
      code: response.code,
      error: response.error || 'Activation request failed',
    };
  }

  return { ok: true, data: response.data };
}

export interface InstalledPolicyPack {
  tenantId: string;
  packId: string;
  version: string;
  canonicalHash: string;
  requestId: string;
  receiptId: string;
  createdAt: string;
  epochAtExecution?: unknown;
  policyPackBefore?: unknown;
}

export interface RequestInstallPolicyPackResponse {
  ok: boolean;
  requestId: string;
  status: 'PENDING';
  artifactId: string;
  packId: string;
  version: string;
  canonicalHash: string;
  correlationId?: string;
}

export async function requestInstallPolicyPack(params: {
  packId: string;
  version: string;
  canonicalHash?: string;
}): Promise<
  | { ok: true; data: RequestInstallPolicyPackResponse }
  | { ok: false; code?: string; error: string; checkoutUrl?: string; moduleId?: string }
> {
  const response = await callFunction<RequestInstallPolicyPackResponse>(
    '/requestInstallPolicyPack',
    'POST',
    {
      packId: params.packId,
      version: params.version,
      canonicalHash: params.canonicalHash,
    }
  );

  if (!response.ok || !response.data) {
    const raw = response.raw && typeof response.raw === 'object' && !Array.isArray(response.raw)
      ? response.raw as Record<string, unknown>
      : {};
    return {
      ok: false,
      code: response.code,
      error: response.error || 'Install request failed',
      checkoutUrl: typeof raw.checkoutUrl === 'string' ? raw.checkoutUrl : undefined,
      moduleId: typeof raw.moduleId === 'string' ? raw.moduleId : undefined,
    };
  }

  return { ok: true, data: response.data };
}

export interface ImportPortablePolicyPackResponse {
  ok: boolean;
  packId: string;
  version: string;
  canonicalHash: string;
  installId: string;
  requestId: string;
  receiptId: string;
  activated: boolean;
  correlationId?: string;
}

export async function importPortablePolicyPack(params: {
  artifact: Record<string, unknown>;
  activate: boolean;
}): Promise<
  | { ok: true; data: ImportPortablePolicyPackResponse }
  | { ok: false; code?: string; error: string }
> {
  const response = await callFunction<ImportPortablePolicyPackResponse>(
    '/importPortablePolicyPack',
    'POST',
    {
      artifact: params.artifact,
      activate: params.activate,
    }
  );

  if (!response.ok || !response.data) {
    return {
      ok: false,
      code: response.code,
      error: response.error || 'Portable policy pack import failed',
    };
  }

  return { ok: true, data: response.data };
}

export interface ActivePolicyPack {
  packId: string;
  policyPackId?: string;
  policyPackHash: string;
  version: string;
  canonicalHash: string;
  receiptId: string;
  activatedAt: string;
  activatedBy: string | null;
  moduleInstallAdmission?: {
    allowedModuleIds: string[];
  };
}

export async function listInstalledPolicyPacks(tenantId: string): Promise<InstalledPolicyPack[]> {
  const ref = query(
    collection(db, 'tenants', tenantId, 'policy_packs_installs'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(ref);
  return snap.docs.map((docSnap) => docSnap.data() as InstalledPolicyPack);
}

export async function fetchActivePolicyPack(tenantId: string): Promise<ActivePolicyPack | null> {
  const ref = doc(db, 'tenants', tenantId, 'policy_packs', 'active');
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as ActivePolicyPack;
}

export function subscribeActivePolicyPack(
  tenantId: string,
  onValue: (value: ActivePolicyPack | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const ref = doc(db, 'tenants', tenantId, 'policy_packs', 'active');
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onValue(null);
        return;
      }
      onValue(snap.data() as ActivePolicyPack);
    },
    (error) => {
      onError?.(error);
    }
  );
}
